import React, { useState } from 'react';
import { useCombatStore } from '../../stores/combatStore';
import { useGameStore } from '../../stores/gameStore';
import { useEncounterStore } from '../../stores/encounterStore';
import { useLootStore } from '../../stores/lootStore';
import { useQuestStore } from '../../stores/questStore';
import { combatService } from '../../services/combatService';
import { encounterService } from '../../services/encounterService';
import { npcService } from '../../services/npcService';
import { useNpcStore } from '../../stores/npcStore';
import { CombatResolveResponseDTO, GameStateSchema } from '@hexcrawl/shared-utils';

interface Props {
  onReturnToLobby: () => void;
}

export const CombatOutcomeOverlay: React.FC<Props> = ({ onReturnToLobby }) => {
  const combatOutcome = useCombatStore((s) => s.combatOutcome);
  const gameState = useGameStore((s) => s.gameState);
  const [loading, setLoading] = useState(false);

  if (!combatOutcome || !gameState?.activeCombat) return null;

  const combatId = gameState.activeCombat.id;

  const transitionToMap = (res: CombatResolveResponseDTO) => {
    const newGameState = GameStateSchema.parse(res);
    useGameStore.getState().setGameState(newGameState);

    // Re-fetch surviving hired NPCs to get their post-combat HP/MP
    const aliveIds = newGameState.hiredNpcIds ?? [];
    if (aliveIds.length > 0) {
      Promise.all(aliveIds.map(id => npcService.getNpcById(id)))
        .then(npcs => useNpcStore.getState().setNpcs(npcs))
        .catch(console.error);
    }

    useCombatStore.getState().reset();

    const game = window.phaserGame;
    if (game?.scene) {
      try { game.scene.stop('CombatScene'); } catch {}
      game.scene.start('MapScene', newGameState);
    }
  };

  const handleResolve = async (action?: 'revive') => {
    setLoading(true);
    try {
      const res = await combatService.resolve(combatId, action);

      if (res.encounter && res.encounter.currentNodeId !== null) {
        const nodeResponse = await encounterService.updateNode(res.encounter.id, null);
        useEncounterStore.getState().setActiveEncounter(nodeResponse.encounter, nodeResponse.node);
        // Companion quest completion chain may have fired, producing loot and quest updates
        if (nodeResponse.pendingLoot) {
          useEncounterStore.getState().setPendingLootOnClose(nodeResponse.pendingLoot);
        }
        if (nodeResponse.quest) {
          useQuestStore.getState().addOrUpdateQuest(nodeResponse.quest);
        }
      } else {
        useEncounterStore.getState().clearActiveEncounter();
      }

      // Surface any loot granted by combat-resolve effects
      if (res.pendingLoot) {
        useLootStore.getState().setPendingLoot(res.pendingLoot);
      }

      transitionToMap(res);
    } catch (e) {
      console.error('Failed to resolve combat:', e);
      setLoading(false);
    }
  };

  const handleQuit = async () => {
    setLoading(true);
    try {
      await combatService.quit(combatId);
      useCombatStore.getState().reset();
      onReturnToLobby();
    } catch (e) {
      console.error('Failed to quit combat:', e);
      setLoading(false);
    }
  };

  return (
    <div className="combat-outcome-overlay">
      <div className="combat-outcome-content">
        <h1 className={`combat-outcome-title ${combatOutcome}`}>
          {combatOutcome === 'victory' ? 'VICTORY' : 'DEFEAT'}
        </h1>

        <div className="combat-outcome-buttons">
          {combatOutcome === 'victory' ? (
            <button
              className="combat-outcome-btn"
              onClick={() => handleResolve()}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Continue'}
            </button>
          ) : (
            <>
              <button
                className="combat-outcome-btn"
                onClick={() => handleResolve('revive')}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Revive'}
              </button>
              <button
                className="combat-outcome-btn"
                onClick={handleQuit}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Quit'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
